import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * EmailPreferencesCard — Per-user opt-out toggles for transactional email.
 *
 * Categories (matching backend):
 *   • enabled  — master switch
 *   • messages — broadcasts & direct messages
 *   • bookings — booking confirmations, enrollment decisions
 *   • billing  — invoices, payment receipts
 *   • system   — password reset / security — informational, NOT mutable
 *                (security mail is always delivered)
 *
 * Used by Org Settings (admin sees their own) AND can be embedded in
 * any future "My Profile" page for parents and staff.
 */
const CATEGORIES = [
  { key: 'messages', label: 'Messages & broadcasts',
    description: 'Emails when an admin or staff sends you a message.' },
  { key: 'bookings', label: 'Bookings & enrollment',
    description: 'Booking confirmations, enrollment decisions, waitlist offers.' },
  { key: 'billing', label: 'Billing & invoices',
    description: 'Payment receipts, invoices, fee reminders.' },
];

export function EmailPreferencesCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    enabled: true, messages: true, bookings: true, billing: true, system: true,
  });

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.getMyEmailPreferences();
      setPrefs((p) => ({ ...p, ...data }));
    } catch (err) {
      toast.error(err.message || 'Failed to load email preferences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      setSaving(true);
      const fresh = await api.updateMyEmailPreferences({ [key]: value });
      setPrefs((p) => ({ ...p, ...fresh }));
    } catch (err) {
      toast.error(err.message || 'Failed to update preference');
      // Roll back optimistic change
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="email-preferences-card">
        <div style={{ padding: '1rem', color: '#6b7280' }}>Loading email preferences…</div>
      </div>
    );
  }

  const masterOff = prefs.enabled === false;

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="email-preferences-card">
      <h3 style={{ margin: '0 0 0.25rem 0' }}>🔔 My Email Notifications</h3>
      <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
        Choose which categories of email you want to receive. Security emails (password resets) are always delivered.
      </p>

      {/* Master switch */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginBottom: '0.75rem',
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Receive emails from this workspace</div>
          <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
            Turn off to silence everything except critical security mail.
          </div>
        </div>
        <Toggle
          testId="email-pref-enabled-toggle"
          checked={prefs.enabled !== false}
          onChange={(v) => handleToggle('enabled', v)}
          disabled={saving}
        />
      </div>

      {/* Category toggles */}
      {CATEGORIES.map((c) => (
        <div
          key={c.key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.6rem 1rem',
            borderRadius: '6px',
            opacity: masterOff ? 0.5 : 1,
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{c.label}</div>
            <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{c.description}</div>
          </div>
          <Toggle
            testId={`email-pref-${c.key}-toggle`}
            checked={prefs[c.key] !== false}
            onChange={(v) => handleToggle(c.key, v)}
            disabled={saving || masterOff}
          />
        </div>
      ))}

      <div style={{
        marginTop: '0.5rem',
        padding: '0.6rem 1rem',
        borderRadius: '6px',
        background: '#f3f4f6',
        color: '#6b7280',
        fontSize: '0.8rem',
      }}>
        🔒 Security & password emails are always sent — they cannot be muted.
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '999px',
        background: checked ? '#10b981' : '#d1d5db',
        border: 'none',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '22px' : '2px',
          width: '20px',
          height: '20px',
          background: '#fff',
          borderRadius: '50%',
          transition: 'left 150ms ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

export default EmailPreferencesCard;
